import { createPinia } from 'pinia'
import { createApp } from 'vue'
import { createRouter, createWebHashHistory } from 'vue-router'
import App from './App.vue'
import './styles/main.css'

// Pretty paths like /login or /login/admin must become hash routes.
// Otherwise Sign In from a /login page turns into /login/admin#/login.
;(() => {
  const path = (window.location.pathname || '/').replace(/\/+$/, '') || '/'
  if (path === '/' || path.startsWith('/api') || path.startsWith('/dist') || /\.[a-z0-9]+$/i.test(path)) {
    return
  }
  const hash = window.location.hash || ''
  const hashRoute = hash.startsWith('#/') ? hash.slice(1) : ''
  let dest = hashRoute
  if (path === '/login/admin' || path.startsWith('/login/admin/')) {
    dest = '/admin/login'
  } else if (path === '/admin' || path === '/admin/login' || path.startsWith('/admin/')) {
    dest = hashRoute.startsWith('/admin') ? hashRoute : (hashRoute === '/login' || hashRoute.startsWith('/login?') ? '/admin/login' : path)
  } else if (!dest) {
    dest = path === '/login' ? '/login' : path
  }
  const search = window.location.search || ''
  window.location.replace(`/${search}#${dest}`)
})()

console.log('main.ts is loading...')

// Add global error handler
window.addEventListener('error', (event) => {
  console.error('Global JavaScript error:', event.error)
  console.error('Error details:', {
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno
  })
})

// Add unhandled promise rejection handler
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
})

// Import pages
import AdminAccountSettings from './components/admin/AdminAccountSettings.vue'
import AdminLoginPage from './components/admin/AdminLoginPage.vue'
import AdminPage from './components/admin/AdminPage.vue'
import AdminPreferences from './components/admin/AdminPreferences.vue'
import AdminProfile from './components/admin/AdminProfile.vue'
import AdminSupport from './components/admin/AdminSupport.vue'
import AIAgentsAdmin from './components/admin/AIAgentsAdmin.vue'
import AmazonAPISettings from './components/admin/AmazonAPISettings.vue'
import BlogAdminPage from './components/admin/BlogAdminPage.vue'
import EmailManagement from './components/admin/EmailManagement.vue'
import JobsAdmin from './components/admin/JobsAdmin.vue'
import PayrollAdmin from './components/admin/PayrollAdmin.vue'
import PlansManagement from './components/admin/PlansManagement.vue'
import SettingsAdmin from './components/admin/SettingsAdmin.vue'
import UsersAdmin from './components/admin/UsersAdmin.vue'
import AdvancedAnalytics from './components/analytics/AdvancedAnalytics.vue'
import SafetyComplianceHub from './components/safety/SafetyComplianceHub.vue'
import AIMarketplacePage from './pages/AIMarketplacePage.vue'
import AIMessagingPage from './pages/AIMessagingPage.vue'
import AITaxOptimizationPage from './pages/AITaxOptimizationPage.vue'
import BankPage from './pages/BankPage.vue'
import CompanyAIMessagingPage from './pages/CompanyAIMessagingPage.vue'
import CompanyAITaxOptimizationPage from './pages/CompanyAITaxOptimizationPage.vue'
import CompanyApplicationsPage from './pages/CompanyApplicationsPage.vue'
import CompanyBankPage from './pages/CompanyBankPage.vue'
import CompanyCrewPage from './pages/CompanyCrewPage.vue'
import CompanyDashboardPage from './pages/CompanyDashboardPage.vue'
import CompanyExpensesPage from './pages/CompanyExpensesPage.vue'
import CompanyJobsPage from './pages/CompanyJobsPage.vue'
import CompanyKnowledgePage from './pages/CompanyKnowledgePage.vue'
import CompanyCrmPage from './pages/CompanyCrmPage.vue'
import CompanyOperationsPage from './pages/CompanyOperationsPage.vue'
import CompanyTankSheetDetailPage from './pages/CompanyTankSheetDetailPage.vue'
import CompanyTankSheetsPage from './pages/CompanyTankSheetsPage.vue'
import CompanyTanksPage from './pages/CompanyTanksPage.vue'
import CompanyWellHeadSheetDetailPage from './pages/CompanyWellHeadSheetDetailPage.vue'
import CompanyWellHeadSheetsPage from './pages/CompanyWellHeadSheetsPage.vue'
import CompanyPaymentsPage from './pages/CompanyPaymentsPage.vue'
import CompanyPayrollPage from './pages/CompanyPayrollPage.vue'
import CompanyContractorPayrollPage from './pages/CompanyContractorPayrollPage.vue'
import CompanyW2DashboardPage from './pages/CompanyW2DashboardPage.vue'
import EmployeeDashboardPage from './pages/EmployeeDashboardPage.vue'
import SupervisorDashboardPage from './pages/SupervisorDashboardPage.vue'
import EmployeeOnSitePage from './pages/EmployeeOnSitePage.vue'
import EmployeeFieldTicketsPage from './pages/EmployeeFieldTicketsPage.vue'
import EmployeeDispatchPage from './pages/EmployeeDispatchPage.vue'
import EmployeeDailyReportPage from './pages/EmployeeDailyReportPage.vue'
import CompanyFieldTicketsPage from './pages/CompanyFieldTicketsPage.vue'
import CompanyFieldTicketDetailPage from './pages/CompanyFieldTicketDetailPage.vue'
import CompanyDispatchPage from './pages/CompanyDispatchPage.vue'
import TicketApprovePage from './pages/TicketApprovePage.vue'
import CompanyManDashboardPage from './pages/CompanyManDashboardPage.vue'
import CompanyManFieldTicketsPage from './pages/CompanyManFieldTicketsPage.vue'
import CompanyManReportsPage from './pages/CompanyManReportsPage.vue'
import CompanyManTanksPage from './pages/CompanyManTanksPage.vue'
import DriverCallsPage from './pages/DriverCallsPage.vue'
import TankWatchPage from './pages/TankWatchPage.vue'
import CompanyPostJobPage from './pages/CompanyPostJobPage.vue'
import CompanyProfilePage from './pages/CompanyProfilePage.vue'
import CompanySafetyPage from './pages/CompanySafetyPage.vue'
import CompanySchedulingPage from './pages/CompanySchedulingPage.vue'
import CompanySettingsPage from './pages/CompanySettingsPage.vue'
import CompanyStorePage from './pages/CompanyStorePage.vue'
import CompanySupportPage from './pages/CompanySupportPage.vue'
import CompanyTaxCalculationPage from './pages/CompanyTaxCalculationPage.vue'
import CompanyTimesheetsPage from './pages/CompanyTimesheetsPage.vue'
import DashboardPage from './pages/DashboardPage.vue'
import ExpensesPage from './pages/ExpensesPage.vue'
import JobDetailPage from './pages/JobDetailPage.vue'
import JobMarketplacePage from './pages/JobMarketplacePage.vue'
import KnowledgePage from './pages/KnowledgePage.vue'
import LoginPage from './pages/LoginPage.vue'

import PaymentsPage from './pages/PaymentsPage.vue'
import ContractorInvoicesPage from './pages/ContractorInvoicesPage.vue'
import PayrollPage from './pages/PayrollPage.vue'

import AdminKnowledgeHub from './components/admin/AdminKnowledgeHub.vue'
import AdminSafetyIncidents from './components/admin/AdminSafetyIncidents.vue'
import RoadmapManagement from './components/admin/RoadmapManagement.vue'
import AIAgenticPage from './pages/AIAgenticPage.vue'
import AITestPage from './pages/AITestPage.vue'
import BlogPage from './pages/BlogPage.vue'
import BlogPostPage from './pages/BlogPostPage.vue'
import CompanyIntegrationsPage from './pages/CompanyIntegrationsPage.vue'
import ContactPage from './pages/ContactPage.vue'
import HomePage from './pages/HomePage.vue'
import IntegrationsPage from './pages/IntegrationsPage.vue'
import LandingPage from './pages/LandingPage.vue'
import LegalPage from './pages/LegalPage.vue'
import DataDeletionPage from './pages/DataDeletionPage.vue'
import LandingSupportPage from './pages/LandingSupportPage.vue'
import ProductViewPage from './pages/ProductViewPage.vue'
import ProfilePage from './pages/ProfilePage.vue'
import RegisterPage from './pages/RegisterPage.vue'
import RoadmapPage from './pages/RoadmapPage.vue'
import SafetyPage from './pages/SafetyPage.vue'
import SchedulingPage from './pages/SchedulingPage.vue'
import SettingsPage from './pages/SettingsPage.vue'
import SimpleTestPage from './pages/SimpleTestPage.vue'
import StorePage from './pages/StorePage.vue'
import SupportPage from './pages/SupportPage.vue'
import TaxCalculationPage from './pages/TaxCalculationPage.vue'
import TimesheetsPage from './pages/TimesheetsPage.vue'
import TokensPage from './pages/TokensPage.vue'

// Import components

// Create router with hash history to prevent white screen
const router = createRouter({
  history: createWebHashHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: LandingPage
    },
    {
      path: '/login',
      name: 'login',
      component: LoginPage
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterPage
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: LegalPage
    },
    {
      path: '/terms',
      name: 'terms',
      component: LegalPage
    },
    {
      path: '/cookies',
      name: 'cookies',
      component: LegalPage
    },
    {
      path: '/data-deletion',
      name: 'data-deletion',
      component: DataDeletionPage
    },
    {
      path: '/delete-my-data',
      redirect: '/data-deletion'
    },

    {
      path: '/dashboard',
      name: 'dashboard',
      component: DashboardPage,
      meta: { requiresAuth: true }
    },
    {
      path: '/jobs',
      name: 'jobs',
      component: JobMarketplacePage
    },
    {
      path: '/job/:id',
      name: 'job-detail',
      component: JobDetailPage
    },

    {
      path: '/company',
      name: 'company',
      component: CompanyDashboardPage
    },
    {
      path: '/company/post-job',
      name: 'company-post-job',
      component: CompanyPostJobPage
    },
    {
      path: '/company/jobs',
      name: 'company-jobs',
      component: CompanyJobsPage
    },
    {
      path: '/company/applications',
      name: 'company-applications',
      component: CompanyApplicationsPage
    },
    {
      path: '/company/crew',
      name: 'company-crew',
      component: CompanyCrewPage
    },
    {
      path: '/company/reports',
      name: 'company-reports',
      component: CompanyDashboardPage
    },
    {
      path: '/company/blog',
      name: 'company-blog',
      component: BlogPage
    },
    {
      path: '/company/marketplace',
      name: 'company-marketplace',
      component: JobMarketplacePage
    },
    {
      path: '/company/ai-messaging',
      name: 'company-ai-messaging',
      component: CompanyAIMessagingPage
    },
    {
      path: '/company/ai-tax-optimization',
      name: 'company-ai-tax-optimization',
      component: CompanyAITaxOptimizationPage
    },
    {
      path: '/company/expenses',
      name: 'company-expenses',
      component: CompanyExpensesPage
    },
    {
      path: '/company/operations',
      name: 'company-operations',
      component: CompanyOperationsPage
    },
    {
      path: '/company/onsite',
      name: 'company-onsite',
      component: CompanyOperationsPage
    },
    {
      path: '/company/field-tickets',
      name: 'company-field-tickets',
      component: CompanyFieldTicketsPage
    },
    {
      path: '/company/field-tickets/:id',
      name: 'company-field-ticket-detail',
      component: CompanyFieldTicketDetailPage
    },
    {
      path: '/company/dispatch',
      name: 'company-dispatch',
      component: CompanyDispatchPage
    },
    {
      path: '/company/crm',
      name: 'company-crm',
      component: CompanyCrmPage
    },
    {
      path: '/company/tank-sheets',
      name: 'company-tank-sheets',
      component: CompanyTankSheetsPage
    },
    {
      path: '/company/tank-sheets/:id',
      name: 'company-tank-sheet-detail',
      component: CompanyTankSheetDetailPage
    },
    {
      path: '/company/tanks',
      name: 'company-tanks',
      component: CompanyTanksPage
    },
    {
      path: '/company/well-head-sheets',
      name: 'company-well-head-sheets',
      component: CompanyWellHeadSheetsPage
    },
    {
      path: '/company/well-head-sheets/:id',
      name: 'company-well-head-sheet-detail',
      component: CompanyWellHeadSheetDetailPage
    },
    {
      path: '/company/safety',
      name: 'company-safety',
      component: CompanySafetyPage
    },
    {
      path: '/company/tax-calculation',
      name: 'company-tax-calculation',
      component: CompanyTaxCalculationPage
    },
    {
      path: '/company/bank',
      name: 'company-bank',
      component: CompanyBankPage
    },
    {
      path: '/company/payments',
      name: 'company-payments',
      component: CompanyPaymentsPage
    },
    {
      path: '/company/payroll',
      name: 'company-payroll',
      component: CompanyPayrollPage
    },
    {
      path: '/company/w2-dashboard',
      name: 'company-w2-dashboard',
      component: CompanyW2DashboardPage
    },
    {
      path: '/company/contractor-payroll',
      name: 'company-contractor-payroll',
      component: CompanyContractorPayrollPage
    },
    {
      path: '/employee',
      name: 'employee-dashboard',
      component: EmployeeDashboardPage
    },
    {
      path: '/supervisor',
      name: 'supervisor-dashboard',
      component: SupervisorDashboardPage
    },
    {
      path: '/employee/onsite',
      name: 'employee-onsite',
      component: EmployeeOnSitePage
    },
    {
      path: '/employee/field-tickets',
      name: 'employee-field-tickets',
      component: EmployeeFieldTicketsPage
    },
    {
      path: '/employee/dispatch',
      name: 'employee-dispatch',
      component: EmployeeDispatchPage
    },
    {
      path: '/employee/daily-report',
      name: 'employee-daily-report',
      component: EmployeeDailyReportPage
    },
    {
      path: '/employee/daily-reports',
      redirect: '/employee/daily-report'
    },
    {
      path: '/employee/tank-sheets',
      name: 'employee-tank-sheets',
      component: CompanyTankSheetsPage
    },
    {
      path: '/employee/tank-sheets/:id',
      name: 'employee-tank-sheet-detail',
      component: CompanyTankSheetDetailPage
    },
    {
      path: '/employee/tanks',
      name: 'employee-tanks',
      component: CompanyTanksPage
    },
    {
      path: '/employee/tank-watch',
      name: 'employee-tank-watch',
      component: TankWatchPage
    },
    {
      path: '/employee/well-head-sheets',
      name: 'employee-well-head-sheets',
      component: CompanyWellHeadSheetsPage
    },
    {
      path: '/employee/well-head-sheets/:id',
      name: 'employee-well-head-sheet-detail',
      component: CompanyWellHeadSheetDetailPage
    },
    {
      path: '/ticket-approve/:token',
      name: 'ticket-approve',
      component: TicketApprovePage
    },
    {
      path: '/company-man',
      name: 'company-man-dashboard',
      component: CompanyManDashboardPage
    },
    {
      path: '/company-man/field-tickets',
      name: 'company-man-field-tickets',
      component: CompanyManFieldTicketsPage
    },
    {
      path: '/company-man/reports',
      name: 'company-man-reports',
      component: CompanyManReportsPage
    },
    {
      path: '/company-man/tanks',
      name: 'company-man-tanks',
      component: CompanyTanksPage
    },
    {
      path: '/company-man/tank-watch',
      name: 'company-man-tank-watch',
      component: TankWatchPage
    },
    {
      path: '/company-man/safety',
      name: 'company-man-safety',
      component: SafetyPage
    },
    {
      path: '/company-man/dashboard',
      redirect: '/company-man'
    },
    {
      path: '/driver',
      name: 'driver-calls',
      component: DriverCallsPage
    },
    {
      path: '/driver/tickets',
      name: 'driver-tickets',
      component: EmployeeFieldTicketsPage
    },
    {
      path: '/driver/tanks',
      name: 'driver-tanks',
      component: TankWatchPage
    },
    {
      path: '/company/timesheets',
      name: 'company-timesheets',
      component: CompanyTimesheetsPage
    },
    {
      path: '/company/scheduling',
      name: 'company-scheduling',
      component: CompanySchedulingPage
    },
    {
      path: '/company/profile',
      name: 'company-profile',
      component: CompanyProfilePage
    },
    {
      path: '/company/settings',
      name: 'company-settings',
      component: CompanySettingsPage
    },
    {
      path: '/company/support',
      name: 'company-support',
      component: CompanySupportPage
    },
    {
      path: '/company/knowledge',
      name: 'company-knowledge',
      component: CompanyKnowledgePage
    },

    {
      path: '/company/store',
      name: 'company-store',
      component: CompanyStorePage
    },

    {
      path: '/payments',
      name: 'payments',
      component: PaymentsPage
    },
    {
      path: '/invoices',
      name: 'contractor-invoices',
      component: ContractorInvoicesPage
    },
    {
      path: '/field-tickets',
      name: 'contractor-field-tickets',
      component: EmployeeFieldTicketsPage
    },
    {
      path: '/onsite',
      name: 'contractor-onsite',
      component: EmployeeOnSitePage
    },
    {
      path: '/dispatch',
      name: 'contractor-dispatch',
      component: EmployeeDispatchPage
    },
    {
      path: '/tank-sheets',
      name: 'contractor-tank-sheets',
      component: CompanyTankSheetsPage
    },
    {
      path: '/tank-sheets/:id',
      name: 'contractor-tank-sheet-detail',
      component: CompanyTankSheetDetailPage
    },
    {
      path: '/tanks',
      name: 'contractor-tanks',
      component: CompanyTanksPage
    },
    {
      path: '/tank-watch',
      name: 'contractor-tank-watch',
      component: TankWatchPage
    },
    {
      path: '/well-head-sheets',
      name: 'contractor-well-head-sheets',
      component: CompanyWellHeadSheetsPage
    },
    {
      path: '/well-head-sheets/:id',
      name: 'contractor-well-head-sheet-detail',
      component: CompanyWellHeadSheetDetailPage
    },
    {
      path: '/payroll',
      name: 'payroll',
      component: PayrollPage
    },
    {
      path: '/timesheets',
      name: 'timesheets',
      component: TimesheetsPage
    },
    {
      path: '/scheduling',
      name: 'scheduling',
      component: SchedulingPage
    },
    {
      path: '/safety',
      name: 'safety',
      component: SafetyPage
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfilePage
    },
    {
      path: '/settings',
      name: 'settings',
      component: SettingsPage
    },
    {
      path: '/store',
      name: 'store',
      component: StorePage
    },
    {
      path: '/product/:id',
      name: 'product-view',
      component: ProductViewPage
    },
    {
      path: '/admin/login',
      name: 'admin-login',
      component: AdminLoginPage
    },
    {
      path: '/admin',
      name: 'admin',
      component: AdminPage
    },
    {
      path: '/admin/profile',
      name: 'admin-profile',
      component: AdminProfile
    },
    {
      path: '/admin/account-settings',
      name: 'admin-account-settings',
      component: AdminAccountSettings
    },
    {
      path: '/admin/preferences',
      name: 'admin-preferences',
      component: AdminPreferences
    },
    {
      path: '/admin/support',
      name: 'admin-support',
      component: AdminSupport
    },
    {
      path: '/admin/blog',
      name: 'blog-admin',
      component: BlogAdminPage
    },
    {
      path: '/admin/users',
      name: 'users-admin',
      component: UsersAdmin
    },
    {
      path: '/admin/jobs',
      name: 'jobs-admin',
      component: JobsAdmin
    },
    {
      path: '/admin/payroll',
      name: 'payroll-admin',
      component: PayrollAdmin
    },
    {
      path: '/admin/email',
      name: 'email-admin',
      component: EmailManagement
    },
    {
      path: '/admin/plans',
      name: 'plans-admin',
      component: PlansManagement
    },
    {
      path: '/admin/analytics',
      name: 'analytics-admin',
      component: AdvancedAnalytics
    },
    {
      path: '/admin/knowledge',
      name: 'knowledge-admin',
      component: AdminKnowledgeHub
    },
    {
      path: '/admin/roadmap',
      name: 'roadmap-admin',
      component: RoadmapManagement
    },
    {
      path: '/admin/safety/incidents',
      name: 'safety-incidents-admin',
      component: AdminSafetyIncidents
    },
    {
      path: '/admin/safety/certifications',
      name: 'safety-certifications-admin',
      component: SafetyComplianceHub
    },
    {
      path: '/admin/safety/training',
      name: 'safety-training-admin',
      component: SafetyComplianceHub
    },
    {
      path: '/admin/ai-agents',
      name: 'ai-agents-admin',
      component: AIAgentsAdmin
    },
    {
      path: '/admin/settings',
      name: 'settings-admin',
      component: SettingsAdmin
    },
    {
      path: '/admin/amazon-api',
      name: 'amazon-api-admin',
      component: AmazonAPISettings
    },

    {
      path: '/ai-messaging',
      name: 'ai-messaging',
      component: AIMessagingPage
    },
    {
      path: '/marketplace',
      name: 'marketplace',
      component: AIMarketplacePage
    },
    {
      path: '/knowledge',
      name: 'knowledge',
      component: KnowledgePage
    },
    {
      path: '/roadmap',
      name: 'roadmap',
      component: RoadmapPage
    },
    {
      path: '/tokens',
      name: 'tokens',
      component: TokensPage
    },
    {
      path: '/contact',
      name: 'contact',
      component: ContactPage
    },
    {
      path: '/support',
      name: 'support',
      component: LandingSupportPage
    },
    {
      path: '/user-support',
      name: 'user-support',
      component: SupportPage
    },
    {
      path: '/tax-calculation',
      name: 'tax-calculation',
      component: TaxCalculationPage
    },
    {
      path: '/expenses',
      name: 'expenses',
      component: ExpensesPage
    },
    {
      path: '/ai-tax-optimization',
      name: 'ai-tax-optimization',
      component: AITaxOptimizationPage
    },
    {
      path: '/bank',
      name: 'bank',
      component: BankPage
    },
    {
      path: '/integrations',
      name: 'integrations',
      component: IntegrationsPage
    },
    {
      path: '/company/integrations',
      name: 'company-integrations',
      component: CompanyIntegrationsPage
    },
    {
      path: '/ai-test',
      name: 'ai-test',
      component: AITestPage
    },
    {
      path: '/simple-test',
      name: 'simple-test',
      component: SimpleTestPage
    },
    {
      path: '/ai-agentic',
      name: 'ai-agentic',
      component: AIAgenticPage
    },
    {
      path: '/landing',
      name: 'landing',
      component: LandingPage
    },
    {
      path: '/home',
      name: 'home-page',
      component: HomePage
    },
    {
      path: '/blog',
      name: 'blog',
      component: BlogPage
    },
    {
      path: '/blog/:id',
      name: 'blog-post',
      component: BlogPostPage
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'not-found',
      redirect: '/'
    }
  ]
})

// A rejected token leaves the UI looking signed in while every API call fails,
// so treat a 401 from our own API as the end of the session — but only when
// that session's token was actually sent. Bare /api/admin/* calls from the
// admin panel (or user pages) must not kick a fresh login back to admin login.
const nativeFetch = window.fetch.bind(window)

const requestAuthToken = (input: RequestInfo | URL, init?: RequestInit): string => {
  const fromHeaders = (headers?: HeadersInit | null): string => {
    if (!headers) return ''
    if (headers instanceof Headers) {
      return headers.get('Authorization') || headers.get('authorization') || ''
    }
    if (Array.isArray(headers)) {
      const row = headers.find(([key]) => String(key).toLowerCase() === 'authorization')
      return row?.[1] || ''
    }
    const rec = headers as Record<string, string>
    return rec.Authorization || rec.authorization || ''
  }
  const raw = fromHeaders(init?.headers) || (input instanceof Request ? fromHeaders(input.headers) : '')
  const match = /Bearer\s+(\S+)/i.exec(raw)
  return match?.[1] || ''
}

window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
  const response = await nativeFetch(input, init)

  if (response.status === 401) {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const isOwnApi = url.includes('/api/') && !/^https?:\/\/(?!oilbuddy\.ai)/i.test(url)
    const hash = window.location.hash
    const onLoginPage = hash === '#/login' || hash.startsWith('#/login?') || hash.startsWith('#/admin/login')

    if (isOwnApi && !url.includes('/api/login') && !url.includes('/api/admin/login') && !onLoginPage) {
      const sent = requestAuthToken(input, init)
      const adminToken = localStorage.getItem('adminToken')
      const userToken = localStorage.getItem('auth_token')
      const onAdminRoute = hash === '#/admin' || hash.startsWith('#/admin/')

      if (adminToken && sent && sent === adminToken) {
        for (const key of ['adminToken', 'adminUser', 'is_admin']) {
          localStorage.removeItem(key)
        }
        if (onAdminRoute) {
          window.location.hash = '#/admin/login'
        }
      } else if (userToken && sent && sent === userToken) {
        for (const key of ['auth_token', 'user_type', 'user_data', 'user_name', 'user_email']) {
          localStorage.removeItem(key)
        }
        const returnTo = hash.slice(1) || '/'
        const safeReturn = returnTo.startsWith('/admin') ? '/dashboard' : returnTo
        window.location.hash = safeReturn === '/dashboard'
          ? '#/login'
          : `#/login?redirect=${encodeURIComponent(safeReturn)}`
      }
    }
  }

  return response
}

// Create Pinia store
const pinia = createPinia()

// Create app
const app = createApp(App)

// Use plugins
app.use(router)
app.use(pinia)

// Add comprehensive route guard
router.beforeEach((to, from, next) => {
  console.log('Router navigating from', from.path, 'to', to.path)
  console.log('Route details:', { 
    toName: to.name, 
    fromName: from.name,
    toFullPath: to.fullPath,
    fromFullPath: from.fullPath,
    meta: to.meta
  })
  
  // Check if route requires admin authentication
  if (to.path.startsWith('/admin') && to.path !== '/admin/login') {
    const adminToken = localStorage.getItem('adminToken')
    const adminUser = localStorage.getItem('adminUser')
    
    // If not authenticated, redirect to admin login
    if (!adminToken || !adminUser) {
      console.log('Redirecting to admin login')
      next('/admin/login')
      return
    }
  }
  
  // Check for protected routes that require authentication
  const protectedRoutes = ['/dashboard', '/company', '/company-man', '/employee', '/supervisor', '/driver', '/payments', '/invoices', '/field-tickets', '/timesheets', '/scheduling', '/safety', '/profile', '/tax-calculation', '/payroll', '/expenses', '/settings', '/knowledge', '/integrations', '/store', '/ai-agentic', '/ai-agents', '/ai-messaging', '/ai-tax-optimization', '/bank', '/marketplace', '/tokens', '/user-support', '/support', '/jobs', '/onsite', '/dispatch', '/tank-sheets', '/tanks', '/tank-watch', '/well-head-sheets']
  const isProtectedRoute = protectedRoutes.some(route => to.path === route || to.path.startsWith(route + '/'))
  const isSupervisorCrew = () => ['supervisor_1', 'supervisor_2', 'supervisor_3'].includes(localStorage.getItem('crew_level') || '')

  const homeFor = (userType: string) => {
    if (userType === 'company') return '/company'
    if (userType === 'employee') return isSupervisorCrew() ? '/supervisor' : '/employee'
    if (userType === 'company_man') return '/company-man'
    if (userType === 'driver') return '/driver'
    return '/dashboard'
  }

  const pathMatches = (path: string, prefix: string) => {
    if (prefix === '/company') {
      return path === '/company' || (path.startsWith('/company/') && !path.startsWith('/company-man'))
    }
    return path === prefix || path.startsWith(prefix + '/')
  }

  const allowedPrefixesFor = (userType: string): string[] => {
    if (userType === 'admin') {
      return [
        '/admin', '/dashboard', '/jobs', '/timesheets', '/payments', '/invoices', '/field-tickets',
        '/onsite', '/dispatch', '/tank-sheets', '/tanks', '/tank-watch', '/well-head-sheets',
        '/payroll', '/scheduling', '/safety', '/profile', '/tax-calculation', '/expenses',
        '/ai-tax-optimization', '/bank', '/ai-messaging', '/ai-agentic', '/ai-agents',
        '/knowledge', '/blog', '/store', '/marketplace', '/integrations', '/settings',
        '/user-support', '/preferences', '/tokens'
      ]
    }
    if (userType === 'company_man') return ['/company-man']
    if (userType === 'driver') return ['/driver']
    if (userType === 'company') return ['/company']
    if (userType === 'employee') {
      return [
        '/employee', '/supervisor', '/timesheets', '/scheduling', '/safety',
        '/profile', '/settings', '/knowledge', '/store', '/marketplace',
        '/integrations', '/ai-messaging', '/ai-agentic', '/ai-agents',
        '/user-support', '/blog', '/preferences'
      ]
    }
    return [
      '/dashboard', '/jobs', '/payments', '/invoices', '/field-tickets',
      '/onsite', '/dispatch', '/tank-sheets', '/tanks', '/tank-watch', '/well-head-sheets',
      '/payroll', '/scheduling', '/safety', '/profile', '/tax-calculation', '/expenses',
      '/ai-tax-optimization', '/bank', '/ai-messaging', '/ai-agentic', '/ai-agents',
      '/knowledge', '/blog', '/store', '/marketplace', '/integrations', '/settings',
      '/user-support', '/preferences', '/tokens'
    ]
  }

  const remapCrossPortal = (userType: string, path: string): string => {
    if (userType === 'company_man') {
      if (path === '/safety' || path.startsWith('/safety/')) return '/company-man/safety'
      if (path === '/employee/tank-watch' || path.startsWith('/employee/tank-watch')) return '/company-man/tank-watch'
      return '/company-man'
    }
    if (userType === 'company') {
      if (path === '/safety' || path.startsWith('/safety/')) return '/company/safety'
      if (path === '/user-support' || path === '/support') return '/company/support'
      if (path === '/profile') return '/company/profile'
      if (path === '/settings') return '/company/settings'
      return '/company'
    }
    if (userType === 'driver') return '/driver'
    return homeFor(userType)
  }
  
  const hasUserSession = () => {
    const authToken = localStorage.getItem('auth_token')
    const userType = localStorage.getItem('user_type')
    const userData = localStorage.getItem('user_data')
    return !!(authToken && userType && userData && userType !== 'admin')
  }

  if (isProtectedRoute) {
    const authToken = localStorage.getItem('auth_token')
    const userType = localStorage.getItem('user_type')
    const userData = localStorage.getItem('user_data')
    
    console.log('Checking authentication for protected route:', to.path)
    console.log('Auth check:', { authToken: !!authToken, userType, hasUserData: !!userData })
    
    // If not authenticated, redirect to login
    if (!hasUserSession()) {
      console.log('Not authenticated, redirecting to login')
      if (userType === 'admin') {
        localStorage.removeItem('user_type')
      }
      if (!authToken || !userType || !userData) {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_type')
        localStorage.removeItem('user_data')
      }
      next('/login')
      return
    }
    
    // Additional validation: check if user type matches route
    if (to.path.startsWith('/company-man') && userType !== 'company_man') {
      next(homeFor(userType))
      return
    }

    if ((to.path === '/invoices' || to.path.startsWith('/field-tickets')) && userType === 'company') {
      next(to.path === '/invoices' ? '/company/payments' : '/company/field-tickets')
      return
    }

    if (to.path.startsWith('/company') && !to.path.startsWith('/company-man') && userType !== 'company') {
      console.log('User type mismatch for company route, redirecting to appropriate dashboard')
      next(homeFor(userType))
      return
    }

    const isTankSetupPath = to.path === '/tanks' || to.path === '/employee/tanks' || to.path === '/company/tanks' || to.path === '/supervisor/tanks'
    if (isTankSetupPath) {
      if (userType === 'company_man') {
        next('/company-man/tanks')
        return
      }
      if (userType === 'employee' && isSupervisorCrew() && (to.path === '/tanks' || to.path === '/supervisor/tanks')) {
        next('/employee/tanks')
        return
      }
      if (!(userType === 'employee' && isSupervisorCrew() && to.path === '/employee/tanks')) {
        next(userType === 'employee' ? '/employee/tank-watch' : homeFor(userType))
        return
      }
    }

    if (to.path.startsWith('/employee') && userType !== 'employee') {
      next(homeFor(userType))
      return
    }

    if (to.path.startsWith('/supervisor') && userType !== 'employee') {
      next(homeFor(userType))
      return
    }

    if (to.path === '/employee' && userType === 'employee' && isSupervisorCrew()) {
      next('/supervisor')
      return
    }

    if (to.path.startsWith('/driver') && userType !== 'driver') {
      next(homeFor(userType))
      return
    }
    
    if (to.path.startsWith('/dashboard') && userType === 'company') {
      console.log('Company user accessing contractor route, redirecting to company dashboard')
      next('/company')
      return
    }

    if (to.path.startsWith('/dashboard') && userType === 'employee') {
      next('/employee')
      return
    }

    if (to.path.startsWith('/dashboard') && userType === 'company_man') {
      next('/company-man')
      return
    }

    if (to.path.startsWith('/dashboard') && userType === 'driver') {
      next('/driver')
      return
    }

    if (!allowedPrefixesFor(userType).some(prefix => pathMatches(to.path, prefix))) {
      next(remapCrossPortal(userType, to.path))
      return
    }
  }
  
  // User login never opens the admin panel. Admin login never opens the user app.
  if (to.path === '/login') {
    const userType = localStorage.getItem('user_type')
    if (userType === 'admin') {
      localStorage.removeItem('user_type')
    }
    if (hasUserSession()) {
      console.log('Authenticated user accessing login, redirecting to dashboard')
      next(homeFor(localStorage.getItem('user_type') || 'contractor'))
      return
    }
  }

  if (to.path === '/admin/login' && localStorage.getItem('adminToken') && localStorage.getItem('adminUser')) {
    next('/admin')
    return
  }
  
  console.log('Router guard: allowing navigation to', to.path)
  next()
})

// Add router error handler
router.onError((error) => {
  console.error('Router error:', error)
})

// Add navigation failure detection
router.afterEach((to, from, failure) => {
  if (failure) {
    console.error('Navigation failed:', failure)
    console.error('Failed navigation details:', {
      from: from.path,
      to: to.path,
      failure: failure
    })
  } else {
    console.log('Router navigation completed from', from.path, 'to', to.path)
    console.log('Current URL:', window.location.href)
  }
})

// Add global navigation failure handler
router.isReady().then(() => {
  console.log('Router is ready')
}).catch((error) => {
  console.error('Router initialization failed:', error)
})

// Mount app with error handling
try {
  app.mount('#app')
  console.log('App mounted successfully')
} catch (error) {
  console.error('Failed to mount app:', error)
  
  // Fallback: show a basic error message
  const appElement = document.getElementById('app')
  if (appElement) {
    appElement.innerHTML = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-family: Inter, sans-serif;
        text-align: center;
        padding: 40px;
      ">
        <div>
          <h2 style="color: #ef4444; margin-bottom: 20px;">Application Error</h2>
          <p>The application failed to load. Please refresh the page.</p>
          <button onclick="window.location.reload()" style="
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-weight: 600;
            margin-top: 20px;
          ">Refresh Page</button>
        </div>
      </div>
    `
  }
}
